import type { RunAction } from '../api/client';
import type { RunState, SupplyKey } from '../api/types';

/**
 * Builders + validity checks for the seven (+item) v1 run actions. These produce
 * exactly the payloads the backend zod `actionSchema` accepts (runs/routes.ts), and
 * the `canX` predicates mirror the engine's legality rules so the UI can disable
 * impossible buttons (the server stays authoritative either way).
 */

export type Direction = 'up' | 'down' | 'left' | 'right';

export const move = (direction: Direction): RunAction => ({ type: 'move', direction });
export const attack = (targetId: string): RunAction => ({ type: 'attack', targetId });
export const wait = (): RunAction => ({ type: 'wait' });
export const descend = (): RunAction => ({ type: 'descend' });
export const extract = (): RunAction => ({ type: 'extract' });
export const pickup = (itemId: string): RunAction => ({ type: 'pickup_item', itemId });
export const discard = (itemId: string): RunAction => ({ type: 'discard_item', itemId });
export const equipFound = (itemId: string): RunAction => ({ type: 'equip_found', itemId });
export const useConsumable = (consumable: SupplyKey): RunAction => ({ type: 'use_consumable', consumable });

const samePos = (a: { x: number; y: number }, b: { x: number; y: number } | null | undefined) =>
  b != null && a.x === b.x && a.y === b.y;

/** Map a movement direction to a delta (matches the engine). */
export function delta(direction: Direction): { x: number; y: number } {
  switch (direction) {
    case 'up': return { x: 0, y: -1 };
    case 'down': return { x: 0, y: 1 };
    case 'left': return { x: -1, y: 0 };
    case 'right': return { x: 1, y: 0 };
  }
}

export function canMove(state: RunState, direction: Direction): boolean {
  const d = delta(direction);
  const next = { x: state.position.x + d.x, y: state.position.y + d.y };
  if (next.x < 0 || next.y < 0 || next.x >= state.floorState.width || next.y >= state.floorState.height) return false;
  return !state.floorState.enemies.some((e) => e.hp > 0 && samePos(e.position, next));
}

export function canExtract(state: RunState): boolean {
  return state.floor >= 3 && samePos(state.position, state.floorState.ferryStep);
}

export function canDescend(state: RunState): boolean {
  return samePos(state.position, state.floorState.descentStair);
}

/** Enemies adjacent to the player (the only legal attack targets). */
export function adjacentEnemies(state: RunState): string[] {
  return state.floorState.enemies
    .filter((e) => e.hp > 0 && Math.abs(e.position.x - state.position.x) + Math.abs(e.position.y - state.position.y) === 1)
    .map((e) => e.id);
}

/** An item drop standing on the player's tile, available for pickup. */
export function pickupHere(state: RunState): string | null {
  const drop = state.floorState.itemDrops.find(
    (d) => d.item.state === 'found' && samePos(d.position, state.position),
  );
  return drop ? drop.item.tempId : null;
}
